import { useState, useCallback } from 'react';
import { UserPlus, Trash2, UserCheck, UserX, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useAdminUsers,
  usePendingUsers,
  useCreateAdminUser,
  useUpdateAdminUser,
  useDeleteAdminUser,
  useApproveUser,
  checkEmailExists,
} from '@/api';
import { useAuthStore } from '@/stores/useAuthStore';
import { Spinner } from '@/components/ui';
import type { AdminUser, CreateAdminUserRequest } from '@/types/admin';
import styles from './AdminUsers.module.css';

const emptyForm: CreateAdminUserRequest = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
};

export function AdminUsers() {
  const { data: users, isLoading } = useAdminUsers();
  const { data: pendingUsers } = usePendingUsers();
  const createUser = useCreateAdminUser();
  const updateUser = useUpdateAdminUser();
  const deleteUser = useDeleteAdminUser();
  const approveUser = useApproveUser();
  const getUser = useAuthStore((s) => s.getUser);
  const currentUser = getUser();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateAdminUserRequest>({ ...emptyForm });
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const handleEmailBlur = useCallback(async () => {
    const email = form.email.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(null);
      return;
    }
    setCheckingEmail(true);
    try {
      const exists = await checkEmailExists(email);
      setEmailError(exists ? 'A user with this email already exists' : null);
    } catch {
      setEmailError(null);
    } finally {
      setCheckingEmail(false);
    }
  }, [form.email]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error('Email and password are required');
      return;
    }
    if (emailError) {
      toast.error(emailError);
      return;
    }
    try {
      await createUser.mutateAsync(form);
      toast.success('Admin user created');
      setForm({ ...emptyForm });
      setShowForm(false);
      setEmailError(null);
    } catch (error: any) {
      const data = error?.response?.data;
      if (data?.email) toast.error(Array.isArray(data.email) ? data.email[0] : data.email);
      else if (data?.password)
        toast.error(Array.isArray(data.password) ? data.password[0] : data.password);
      else toast.error('Failed to create user');
    }
  };

  const handleToggleActive = async (user: AdminUser) => {
    if (user.email === currentUser?.email) {
      toast.error('You cannot deactivate your own account');
      return;
    }
    try {
      await updateUser.mutateAsync({ id: user.id, is_active: !user.is_active });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
    } catch {
      toast.error('Failed to update user');
    }
  };

  const handleDelete = async (user: AdminUser) => {
    if (user.email === currentUser?.email) {
      toast.error('You cannot delete your own account');
      return;
    }
    try {
      await deleteUser.mutateAsync(user.id);
      toast.success('User deleted');
      setConfirmDeleteId(null);
    } catch {
      toast.error('Failed to delete user');
    }
  };

  const handleApprove = async (user: AdminUser) => {
    try {
      await approveUser.mutateAsync(user.id);
      toast.success(`${user.email} approved as admin`);
    } catch {
      toast.error('Failed to approve user');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Admin Users</h1>
          <p className={styles.subtitle}>{users?.length || 0} admin users</p>
        </div>
        <button className={styles.addButton} onClick={() => setShowForm(!showForm)}>
          <UserPlus size={16} />
          Add User
        </button>
      </div>

      {showForm && (
        <form className={styles.form} onSubmit={handleCreate}>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label className={styles.label}>Email *</label>
              <input
                type="email"
                className={`${styles.input} ${emailError ? styles.inputError : ''}`}
                value={form.email}
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value });
                  setEmailError(null);
                }}
                onBlur={handleEmailBlur}
                placeholder="admin@example.com"
                required
              />
              {checkingEmail && <span className={styles.hint}>Checking...</span>}
              {emailError && <span className={styles.errorHint}>{emailError}</span>}
            </div>
            <div className={styles.formField}>
              <label className={styles.label}>Password *</label>
              <input
                type="password"
                className={styles.input}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min 5 characters"
                required
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.label}>First Name</label>
              <input
                type="text"
                className={styles.input}
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                placeholder="First name"
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.label}>Last Name</label>
              <input
                type="text"
                className={styles.input}
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                placeholder="Last name"
              />
            </div>
          </div>
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => {
                setShowForm(false);
                setForm({ ...emptyForm });
                setEmailError(null);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={createUser.isPending || !!emailError}
            >
              {createUser.isPending ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      )}

      {/* Pending approval section */}
      {pendingUsers && pendingUsers.length > 0 && (
        <>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Pending Approval</h2>
            <span className={styles.badge}>{pendingUsers.length}</span>
          </div>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className={styles.userCell}>
                        <div className={`${styles.avatar} ${styles.avatarPending}`}>
                          {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                        </div>
                        <div>
                          <div className={styles.userName}>
                            {user.first_name || user.last_name
                              ? `${user.first_name} ${user.last_name}`.trim()
                              : user.email.split('@')[0]}
                          </div>
                          <div className={styles.userEmail}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className={styles.dateCell}>{formatDate(user.date_joined)}</td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={`${styles.approveBtn}`}
                          onClick={() => handleApprove(user)}
                          title="Approve as Admin"
                        >
                          <ShieldCheck size={15} />
                          Approve
                        </button>
                        {confirmDeleteId === user.id ? (
                          <div className={styles.confirmDelete}>
                            <button
                              className={styles.confirmYes}
                              onClick={() => handleDelete(user)}
                            >
                              Confirm
                            </button>
                            <button
                              className={styles.confirmNo}
                              onClick={() => setConfirmDeleteId(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            onClick={() => setConfirmDeleteId(user.id)}
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Admin users section */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Admin Users</h2>
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>User</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users?.map((user) => {
              const isSelf = user.email === currentUser?.email;
              return (
                <tr key={user.id} className={!user.is_active ? styles.inactive : ''}>
                  <td>
                    <div className={styles.userCell}>
                      <div className={styles.avatar}>
                        {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                      </div>
                      <div>
                        <div className={styles.userName}>
                          {user.first_name || user.last_name
                            ? `${user.first_name} ${user.last_name}`.trim()
                            : user.email.split('@')[0]}
                          {isSelf && <span className={styles.youBadge}>You</span>}
                        </div>
                        <div className={styles.userEmail}>{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${user.is_active ? styles.active : styles.deactivated}`}
                    >
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className={styles.dateCell}>{formatDate(user.date_joined)}</td>
                  <td className={styles.dateCell}>{formatDate(user.last_login)}</td>
                  <td>
                    {!isSelf && (
                      <div className={styles.actions}>
                        <button
                          className={`${styles.actionBtn} ${user.is_active ? styles.deactivateBtn : styles.activateBtn}`}
                          onClick={() => handleToggleActive(user)}
                          title={user.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {user.is_active ? <UserX size={15} /> : <UserCheck size={15} />}
                        </button>
                        {confirmDeleteId === user.id ? (
                          <div className={styles.confirmDelete}>
                            <button
                              className={styles.confirmYes}
                              onClick={() => handleDelete(user)}
                            >
                              Confirm
                            </button>
                            <button
                              className={styles.confirmNo}
                              onClick={() => setConfirmDeleteId(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            onClick={() => setConfirmDeleteId(user.id)}
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(!users || users.length === 0) && (
          <div className={styles.empty}>No admin users found.</div>
        )}
      </div>
    </div>
  );
}
